import { HttpClient, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as async from 'async';
import { SharedUrlRoutes, UtilHttpStatus } from '@formbird/shared';
import {isArray, isString, cloneDeep} from 'lodash';
import { OfflineStatusService } from '../offline-status/offline-status.service';
import { ClientConstants } from '../../constants/ClientConstants';
import { IndexedDBConstants } from '../../constants/IndexedDBConstants';
import { BroadcastService } from '../broadcast/broadcast.service';
import { FileProviderService } from '../file/file-provider.service';
import { IndexedDBService } from './indexed-db.service';
import { IndexedDBConnectorService } from './indexed-dbconnector.service';

const serverRoutes = SharedUrlRoutes.serverRoutes;
const logger = console;

@Injectable({
  providedIn: 'root'
})
export class IndexedDBQueueService {

  constructor(
    private http: HttpClient,
    private indexedDBConnectorService: IndexedDBConnectorService,
    private indexedDBService: IndexedDBService,
    private offlineStatusService: OfflineStatusService,
    private broadcastService: BroadcastService,
    private fileProviderService: FileProviderService
  ) { }

  /**
   * send offline data that is saved in IndexedDB queue
   * @param cachedObject - the document to update
   * @param offlineDetails - details of the operation
   */
  private sendOfflineData(cachedObject, offlineDetails): Promise<any> {

    if (!cachedObject) {
      return Promise.reject(new Error('Document not provided in sendOflineData'));
    }

    if (!offlineDetails) {
      return Promise.reject(new Error('Offline details not provided in sendOfflineData'));
    }

    const document = cloneDeep(cachedObject); // only send data and avoid updating on passed data

    let pushedData = document;

    let url = null;

    const operationType = offlineDetails.type;

    switch (operationType) {

      case ClientConstants.OPERATION_TYPE_POST:
        url = serverRoutes.document;
        if (document.systemHeader.currentVersion === false){
          document.systemHeader.currentVersion = true;
        }
        break;

      case ClientConstants.OPERATION_TYPE_PUT:

        // allows sending an old version into the server to replace the current version there
        // it will later then be overrideen when newer versions are submitted.
        // This assumes that updates are submitted only after the previous request succeeded.
        document.systemHeader.currentVersion = true;

        url = serverRoutes.documentDeepDiffUpdate + '/' + document.documentId;

        const options = {
          updates: offlineDetails.updates,
          isUpdateFromPreviousVersion: offlineDetails.isUpdateFromPreviousVersion,
          makeCurrentVersion: offlineDetails.makeCurrentVersion,
          importPreviousVersion: offlineDetails.importPreviousVersion
        };

        pushedData = {
          document: document,
          options: options
        };
        break;

      case ClientConstants.OPERATION_TYPE_DELETE:
        url = serverRoutes.document +
          '/' + document.documentId +
          '/' + document.systemHeader.templateId +
          '/' + document.systemHeader.versionId;
        break;

      default:
        logger.error('Operation not yet implemented.');
        break;
    }

    const req = new HttpRequest(operationType, url, pushedData);

    return this.http.request(req).toPromise();
  }

  private doUpdateCachedObject(cachedObject) {

    const _self = this;

    return new Promise((resolve, reject) => {

      _self.indexedDBService.saveEntry(IndexedDBConstants.DOCUMENT_TABLE_NAME, cachedObject, function (err) {

        if (!err) {
          resolve();

        } else {
          reject();
        }
      });

    });

  }

  private doUpdateFileToEachCachedObject(cachedDoc, processedFiles) {

    let isSetValue = false;

    processedFiles.forEach(function (processedFile) {

      if (processedFile.serverAssignedFileNo) {

        const fieldName = processedFile.uploadConfig.data.docFieldName;
        const fieldValue = cachedDoc[fieldName];

        if (fieldValue && fieldValue.length) {
          if (isArray(fieldValue)) {

            for (let i = 0; i < fieldValue.length; i++) {

              if (fieldValue[i].fileNo === processedFile.fileNo) {
                fieldValue[i].serverAssignedFileNo = processedFile.serverAssignedFileNo;

                isSetValue = true;
              }
            }

          } else if (isString(fieldValue)) {

            cachedDoc[fieldName] = processedFile.serverAssignedFileNo;
            isSetValue = true;
          }
        }

      }
    });

    return isSetValue;
  }

  private updateSentFileToOfflineDoc(docId, processedFiles) {

    const _self = this;

    return new Promise((resolve, reject) => {

      // Find the documents in offline DB that contains this file to update the serverAssignedFileNo
      const indexValue = docId;
      if (indexValue) {

        const tblName = IndexedDBConstants.DOCUMENT_TABLE_NAME;
        const indexName = IndexedDBConstants.DOCUMENT_INDEX_DOCUMENT_ID;

        _self.indexedDBService.getEntries(tblName, indexName, indexValue, function (cachedObjects) {

          const subPromises = [];

          for (let id = 0; id < cachedObjects.length; id++) {
            const cachedObject = cachedObjects[id];

            const isSetValue = _self.doUpdateFileToEachCachedObject(cachedObject, processedFiles);
            if (isSetValue) {

              logger.info('Update cached object for file attach.');
              logger.info(cachedObject);

              subPromises.push(_self.doUpdateCachedObject(cachedObject));
            }
          }

          Promise.all(subPromises).then(
            function successFunc() {
              resolve();
            },
            function errorFunc() {
              reject();
            }
          );

        });

      } else {
        resolve();
      }

    });

  }

  private updateSentFileToOfflineDocs(processedFiles) {

    const promises = [];
    const self = this;
    const docMap = {};
    processedFiles.forEach(function (processedFile) {

      const docId = processedFile.uploadConfig ? processedFile.uploadConfig.data.documentId : null;

      if (docId) {

        if (!docMap[docId]) {
          docMap[docId] = [];
        }

        docMap[docId].push(processedFile);
      }
    });

    const docIds = Object.keys(docMap);
    docIds.forEach(function (docId) {
      promises.push(self.updateSentFileToOfflineDoc(docId, docMap[docId]));
    });

    return Promise.all(promises);
  }

  /*
   * @param onItemProgress - Function to call for every progress of an item regardless if it failed or not
   * This will be used to update the poller's lastActive status and make the poller active
   * @return {promise}
   */
  sendUnsyncedObjects(onItemProgress) {

    const _self = this;

    return new Promise((resolve, reject) => {

      let documentUploadDone = false; // flag
      let abort = false; // used to abort the sequence if client is in offline mode or server responds with status -1

      _self.indexedDBService.getUnsentOfflineFilesInQueue(function (files) {

        if (files.length === 0) {
          sendUnProcessedDocs();

        } else {

          logger.info('Sending unsent offline files...');

          const processedFiles = [];
          let processedCount = 0;
          for (let x = 0; x < files.length; x++) {
            const file = files[x];

            _self.processOfflineUpload(file, onItemProgress, function onItemProcessed(status, fileResult) {

              processedCount++;

              if (fileResult || fileResult === false) {

                processedFiles.push(fileResult);
                _self.broadcastService.broadcast(ClientConstants.FILE_SENT_EVENT_NAME, fileResult);

              } else if (status === _self.indexedDBService.constants.OFFLINE_STATUS_PENDING) {

                documentUploadDone = true;
                terminatePromise();

              }

              if (processedCount === files.length) {
                logger.info('All unsent offline files have been sent. Do update unprocessed docs.');

                _self.updateSentFileToOfflineDocs(processedFiles).then(

                  function sendOfflineDocs() {
                    logger.info('All uploaded files are put into unprocessed docs. Sent these docs.');
                    sendUnProcessedDocs();
                  },

                  function error() {
                    documentUploadDone = true;
                    terminatePromise();
                  }
                );
              }
            });
          }
        }
      });

      function sendUnProcessedDocs() {
        _self.indexedDBService.getUnprocessedDocs(function (cachedObjects) {

          _self.offlineStatusService.setPendingDocumentCount(cachedObjects.length);

          if (cachedObjects.length === 0) {
            documentUploadDone = true;
            terminatePromise();
            return;
          }

          logger.info('Sending unprocessed documents..');

          let documentProcessed = 0;

          async.whilst(hasMoreDocuments, iterator, callbackFn);

          function hasMoreDocuments() { // async.whilst first param - if true then continue
            return !abort && documentProcessed < cachedObjects.length;
          }

          function iterator(callback) { // async.whilst second param

            const cachedObject = cachedObjects[documentProcessed];
            const docId = cachedObject.documentId;
            const docVersionId = cachedObject.systemHeader.versionId;

            _self.indexedDBService.loadCachedDocByVersion(docId, docVersionId, function (dbCachedObject) {
              const deleteOnComplete =
                dbCachedObject.systemHeader.offlineDetails.deleteOnComplete;

              // do not send the offlineDetails to the server because the server does not use these values. The
              // updates can cause ElasticSearch mapping conflicts
              const offlineDetails = dbCachedObject.systemHeader.offlineDetails;
              delete dbCachedObject.systemHeader.offlineDetails;

              _self.sendOfflineData(dbCachedObject, offlineDetails).then(
                function success(response) {

                  _self.offlineStatusService.setConnected();

                  onItemProgress(); // record activity

                  documentProcessed++;

                  let versionConflict = false;
                  const thisDcVersionId = dbCachedObject.systemHeader.versionId;

                  if (response.data != null && response.data._id != null) {

                    const expectedVersion = thisDcVersionId;
                    const retrievedVersion = response.data.systemHeader.versionId;
                    versionConflict = expectedVersion !== retrievedVersion;

                    /**
                     * update the cache with the actual document in server
                     * if there are conflicts as stated in issue 10049 (versionConflict === true),
                     * the document response.data will not be identical with the sent document.
                     * Whatever the server sent back, response.data in this case, will always be
                     * the current document.
                     */

                    dbCachedObject = response.data;
                  }

                  const tbl = _self.indexedDBService.constants.DOCUMENT_TABLE_NAME;

                  if (deleteOnComplete) {
                    const index = _self.indexedDBService.constants.DOCUMENT_INDEX_VERSION_ID;
                    // will delete only a single entry since versionID is our primary key
                    _self.indexedDBService.deleteEntries(tbl, index, thisDcVersionId, null);

                  } else {

                    // update the doc - the offlineDetails need to be put back into the document before updating
                    dbCachedObject.systemHeader.offlineDetails = offlineDetails;
                    dbCachedObject.systemHeader.offlineDetails.status =
                      _self.indexedDBService.constants.OFFLINE_STATUS_UPLOADED;
                    dbCachedObject.systemHeader.offlineDetails.tries += 1;
                    _self.indexedDBService.saveEntry(tbl, dbCachedObject);
                  }

                  _self.offlineStatusService.decPendingDocumentCount(); // update status bar

                  if (versionConflict) {
                    // Remove all versions of this document to retain only the
                    // most recent curent version from the server.
                    _self.indexedDBService.documentReset(dbCachedObject.documentId);

                    logger.info('A new version is available for document ' + dbCachedObject.documentId);
                    // TODO: inform a service to reload the current document offline safe
                  }

                  // The save from server always returns current version
                  // (even the offline version is non-current), then the document is cached. It causes
                  // many current versions of one document in indexedDB.
                  // This will load all other uploaded current versions and update them to
                  // non-current version.
                  _self.indexedDBService.getProcessedCurrentVersionDocs(function (savedEntries) {

                    if (savedEntries && savedEntries.length) {

                      for (let i = 0; i < savedEntries.length; i++) {

                        const objectData = savedEntries[i];

                        if (objectData
                          && objectData.documentId === dbCachedObject.documentId
                          && objectData.systemHeader
                          && objectData.systemHeader.currentVersion
                          && objectData.systemHeader.versionId !== thisDcVersionId) {

                          objectData.systemHeader.currentVersion = false;

                          logger.info('Set other cached current processed versions of the current ' +
                            'document to non current version: ' + objectData.systemHeader.versionId);

                          _self.indexedDBService.saveEntry(tbl, savedEntries[i]);

                        }
                      }
                    }

                    callback();

                  });
                },

                function err(response) {

                  onItemProgress();

                  offlineDetails.tries += 1;
                  offlineDetails.lastRequestStatus = UtilHttpStatus.getStatusText(response.status);

                  if (response.status === UtilHttpStatus.SERVER_UNRESPONSIVE) { // item will be retrieved
                    _self.offlineStatusService.setDisconnected();
                    abort = true;

                  } else { // other errors will not be retried

                    documentProcessed++;
                    offlineDetails.status = _self.indexedDBService.constants.OFFLINE_STATUS_FAILED;

                    const errMsg = 'Error sending document. Server responded with status: ' + response.status;
                    logger.error(errMsg);

                    _self.offlineStatusService.decPendingDocumentCount();
                  }

                  // put the offlineDetails back into the document before saving to IndexedDB
                  dbCachedObject.systemHeader.offlineDetails = offlineDetails;

                  _self.indexedDBService.saveEntry(_self.indexedDBService.constants.DOCUMENT_TABLE_NAME, dbCachedObject);

                  // abort only if error can be retried, else continue on the next item
                  callback(abort ? abort : null);

                }
              );
            });
          }

          function callbackFn(error) { // called after whilst completes
            documentUploadDone = true;
            terminatePromise();
          }

        });
      }

      // either the document upload or the file upload will terminate this process, depending on who completes last
      function terminatePromise() {

        if (!documentUploadDone) { // wait for docs to finish uploading
          return;
        }

        // Document upload is sequential so it has to abort if one item fails with a retriable error
        // But files can be uploaded anytime, if the upload fails it will just be retried. Unless
        // the error is a fatal one, which will not be retried and marked as OFFLINE_STATUS_FAILED
        if (abort) {
          reject(new Error('Client is offline.'));
        } else {
          resolve();
        }
      }

    });

  }

  // moved into a function to isolate it and avoid context issues
  private processOfflineUpload(record, onItemProgress, onItemProcessed) {

    const _self = this;

    _self.fileProviderService.onlineFileUpload(record.uploadConfig).then(successF, errorF);

    function successF(response) {
      onItemProgress(); // keep alive poller

      const result = response.data;
      if (!result) {
        onItemProcessed(null, false); // false to ignore this file when changing its info in document
        logger.info('Error in File Upload. Did not receive appropriate response.');
        return;
      }

      if (response.data.fileNo) {
        record.serverAssignedFileNo = response.data.fileNo;
      }

      // update the offline entry status to UPLOADED status
      record.status = _self.indexedDBService.constants.OFFLINE_STATUS_UPLOADED;
      _self.indexedDBService.saveFile(record);

      logger.info('File ' + record.fileNo + ' successfully uploaded via offline poller.');

      onItemProcessed(null, record);

    }

    function errorF(response) {

      onItemProgress(); // keep alive poller

      let entryStatus;

      if (response.status === UtilHttpStatus.SERVER_UNRESPONSIVE) { // ignore these errors retry sending again
        entryStatus = _self.indexedDBService.constants.OFFLINE_STATUS_PENDING;

      } else { // else shall never be retried
        entryStatus = _self.indexedDBService.constants.OFFLINE_STATUS_FAILED;
      }

      record.status = entryStatus;

      // update the offline entry
      const errMsg = response.status + ': ' + UtilHttpStatus.getStatusText(response.status);
      const errCount = record.errorCount == null ? 1 : record.errorCount + 1;
      _self.indexedDBService.saveFile(record, errMsg, errCount);

      onItemProcessed(entryStatus);
    }

    function progressF(response) {
      // TODO: add progress indicator later
      onItemProgress();
    }
  }
}
